/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { jsx } from '@compiled/react';

import { NavigationAnalyticsContext } from '@atlaskit/analytics-namespaced-context';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';
import { type ContentProps, Popup, type TriggerProps } from '@atlaskit/popup';
import { BFFConsumerEnum, BFFProvider } from '@atlassian/navigation-bff-data-provider';

import { MenuControlContext } from '../context';
import { useUniversalCreateAnalytics } from '../hooks';
import type { CreateContentProps, UniversalCreateButtonProps } from '../types';
import type { UniversalCreateButtonPropsV2 } from '../types/buttonV2';
import { findSelectedItem, findSelectedItemV2, useNormalizeItems } from '../utils';

import {
	MenuContent,
	MenuWrapper,
	PopupWrapper,
	TopLevelWrappers,
	TriggerButton,
} from './components';
import { MenuContentV2 } from './components/MenuContentV2';
import { useIsResponsiveView } from './utils';

// ============================================================================
// Type Definitions for Combined Props Support
// ============================================================================

/**
 * Combined props type that supports both v1 and v2 APIs.
 * Uses discriminated union based on the presence of 'items' (v1) or 'sections' (v2).
 */
type UniversalCreateButtonCombinedProps = UniversalCreateButtonProps | UniversalCreateButtonPropsV2;

/**
 * Type guard to check if props are v1 (has 'items' property)
 */
function isV1Props(props: UniversalCreateButtonCombinedProps): props is UniversalCreateButtonProps {
	return 'items' in props;
}

/**
 * Type guard to check if props are v2 (has 'sections' property)
 */
function isV2Props(
	props: UniversalCreateButtonCombinedProps,
): props is UniversalCreateButtonPropsV2 {
	return 'sections' in props;
}

const FlagsWrapper = ({ children }: { children: ReactNode }) => {
	if (fg('navx_uc_platformisation_forms_enabled')) {
		return <FlagsProvider>{children}</FlagsProvider>;
	}
	return children;
};

export function UniversalCreateButton(props: UniversalCreateButtonCombinedProps) {
	// ============================================================================
	// Props Extraction with Version Detection
	// ============================================================================

	// Extract common props (available in both v1 and v2)
	const {
		children,
		testId,
		shouldRenderToParent,
		onClick,
		onClose,
		selectedItem,
		product,
		subProduct,
		isOpen: controlledOpen = false,
		enableTriggerPulse = false,
		triggerButtonSpotlightTarget,
		isNav4Enabled = true,
		buttonTheme,
		preventClose = false,
		onMouseEnter,
		onMouseLeave,
	} = props;

	// Extract version-specific props with type narrowing
	// V1 props
	const items = isV1Props(props) ? props.items : undefined;
	const suggestionConfig = isV1Props(props)
		? (props.suggestionConfig ?? ({ enabled: false } as const))
		: ({ enabled: false } as const);

	// V2 props
	const isV2Mode = fg('navx_uc_cross_app_creation_v2_enabled') && isV2Props(props);
	const sections = useMemo(() => (isV2Mode ? props.sections : []), [isV2Mode, props]);
	const crossAppCreation = isV2Mode
		? (props.crossAppCreation ?? ({ enabled: false } as const))
		: ({ enabled: false } as const);

	// ============================================================================
	// Component Logic (unchanged from original)
	// ============================================================================
	const isResponsive = useIsResponsiveView();
	const [isOpen, setIsOpen] = useState(controlledOpen || false);

	// Normalize items for v1, or use empty array for v2
	const structuredItems = useNormalizeItems(items ?? []);
	const defaultMenu = useCallback(
		() => (isV2Mode ? <MenuContentV2 /> : <MenuContent />),
		[isV2Mode],
	);
	const [createContent, setCreateContent] = useState<(contentProps: ContentProps) => ReactNode>(
		() => defaultMenu,
	);
	const { fireMenuStateChanged } = useUniversalCreateAnalytics();

	const reset = useCallback(() => {
		setCreateContent(() => defaultMenu);
	}, [defaultMenu]);

	const close = useCallback(() => {
		setCreateContent(() => defaultMenu);
		setIsOpen(false);
	}, [defaultMenu]);

	const load = useCallback(
		(loadCreateContent: CreateContentProps) => {
			setCreateContent(
				() => (contentProps: ContentProps) =>
					loadCreateContent(contentProps, {
						reset,
						close,
					}),
			);
		},
		[close, reset],
	);

	const triggerButtonClick = useCallback(() => {
		setIsOpen((prev) => {
			if (prev) {
				onClose?.();
			}
			return !prev;
		});

		onClick?.();
	}, [onClick, onClose, setIsOpen]);

	const popupTrigger = useCallback(
		(triggerProps: TriggerProps) => (
			<TriggerButton
				testId={testId}
				triggerButtonSpotlightTarget={triggerButtonSpotlightTarget}
				enableTriggerPulse={enableTriggerPulse}
				isOpen={isOpen}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				onClick={triggerButtonClick}
				triggerProps={triggerProps}
				isNav4Enabled={isNav4Enabled}
				buttonTheme={buttonTheme}
			>
				{children}
			</TriggerButton>
		),
		[
			children,
			enableTriggerPulse,
			isOpen,
			testId,
			triggerButtonClick,
			triggerButtonSpotlightTarget,
			buttonTheme,
			isNav4Enabled,
			onMouseEnter,
			onMouseLeave,
		],
	);

	const onCloseMenu = useCallback(() => {
		if (product === 'confluence' && preventClose) {
			return;
		}

		setIsOpen(false);
		setCreateContent(() => defaultMenu);
		onClose?.();
	}, [defaultMenu, onClose, product, preventClose]);

	const foundItem = useMemo(() => {
		if (!selectedItem) {
			return null;
		}
		if (isV2Mode) {
			return findSelectedItemV2(sections, selectedItem);
		}
		return findSelectedItem(structuredItems, selectedItem);
	}, [selectedItem, isV2Mode, sections, structuredItems]);

	useEffect(() => {
		if (isOpen) {
			const loadContent = foundItem?.loadCreateContent;
			if (loadContent) {
				setCreateContent(
					() => (contentProps: ContentProps) =>
						loadContent(contentProps, {
							reset,
							close,
						}),
				);
			} else {
				reset();
			}
		}
	}, [close, isOpen, foundItem?.loadCreateContent, reset]);

	useEffect(() => {
		if (controlledOpen !== undefined) {
			setIsOpen(controlledOpen);
		}
	}, [controlledOpen]);

	const prevIsOpenRef = useRef<boolean>(isOpen);

	useEffect(() => {
		if (fg('navx-uc-unified-instrumentation')) {
			const prevIsOpen = prevIsOpenRef.current;

			// Only fire analytics on actual state transitions
			if (isOpen !== prevIsOpen) {
				fireMenuStateChanged({ itemState: isOpen ? 'expanded' : 'collapsed' });
			}
			prevIsOpenRef.current = isOpen;
		}
	}, [isOpen, fireMenuStateChanged, prevIsOpenRef]);

	const loadPopupContent = useCallback(
		(props: ContentProps) => <MenuWrapper>{createContent(props)}</MenuWrapper>,
		[createContent],
	);

	const AnalyticsWrapper =
		FeatureGates.initializeCalled() &&
		FeatureGates.getExperimentValue(
			'platform_fix_create_button_mutation_element',
			'isEnabled',
			false,
		)
			? AnalyticsWrapperComponent
			: ({ children }: { children: ReactNode }) => {
					if (fg('navx-1690-analytics-context-wrapper')) {
						return (
							<NavigationAnalyticsContext data={{ source: 'topNav' }}>
								{children}
							</NavigationAnalyticsContext>
						);
					}
					return children;
				};

	const popup = (
		<Popup
			isOpen={isOpen}
			placement={isResponsive && isNav4Enabled ? 'bottom-end' : 'bottom-start'}
			// eslint-disable-next-line @atlaskit/design-system/use-should-render-to-parent
			shouldRenderToParent={shouldRenderToParent}
			onClose={onCloseMenu}
			content={loadPopupContent}
			trigger={popupTrigger}
		/>
	);

	// ============================================================================
	// MenuControlContext Value Construction
	// Populate context with appropriate values based on props version
	// ============================================================================

	const menuControlContextValue = {
		reset,
		close,
		load,
		// V1 fields
		mixedItems: structuredItems,
		suggestionConfig: suggestionConfig ?? { enabled: false },
		// V2 fields
		sections,
		crossAppCreationConfig: crossAppCreation ?? { enabled: false },
		// Common fields
		product,
		subProduct,
		isNav4Enabled,
	};

	if (fg('navx_uc_top_level_wrappers_refactor')) {
		return (
			<MenuControlContext.Provider value={menuControlContextValue}>
				<TopLevelWrappers>{popup}</TopLevelWrappers>
			</MenuControlContext.Provider>
		);
	}

	return (
		<BFFProvider consumer={BFFConsumerEnum.UNIVERSAL_CREATE}>
			<MenuControlContext.Provider value={menuControlContextValue}>
				<PopupWrapper>
					<FlagsWrapper>
						<AnalyticsWrapper>{popup}</AnalyticsWrapper>
					</FlagsWrapper>
				</PopupWrapper>
			</MenuControlContext.Provider>
		</BFFProvider>
	);
}

const AnalyticsWrapperComponent = ({ children }: { children: ReactNode }) => {
	if (fg('navx-1690-analytics-context-wrapper')) {
		return (
			<NavigationAnalyticsContext data={{ source: 'topNav' }}>
				{children}
			</NavigationAnalyticsContext>
		);
	}
	return children;
};

export default UniversalCreateButton;
